<template>
  <!-- lang so the uppercase eyebrow follows Turkish casing (i → İ). -->
  <section class="settings" :lang="locale">
    <header class="settings__head">
      <h1 class="settings__title">{{ $t('settings.heading') }}</h1>
      <p class="settings__lead">{{ $t('settings.lead') }}</p>
    </header>

    <div v-if="state === 'loading'" class="settings__panel settings__panel--quiet" aria-busy="true">
      <span class="settings__spinner" aria-hidden="true" />
      <span>{{ $t('settings.loading') }}</span>
    </div>

    <div v-else-if="state === 'error'" class="settings__panel settings__panel--quiet settings__panel--error" role="alert">
      <span class="settings__note--error" aria-hidden="true">✕</span>
      <span>{{ $t('settings.loadFailed') }}</span>
    </div>

    <div v-else-if="state === 'signedOut'" class="settings__panel settings__signin">
      <span class="settings__signin-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" /></svg>
      </span>
      <h2 class="settings__signin-title">{{ $t('settings.signInTitle') }}</h2>
      <p class="settings__desc">{{ $t('settings.signInBody') }}</p>
      <ul class="settings__perks">
        <li><span class="settings__perk-tick" aria-hidden="true">✔</span>{{ $t('settings.signInPerkEmail') }}</li>
        <li><span class="settings__perk-tick" aria-hidden="true">✔</span>{{ $t('settings.signInPerkDashboard') }}</li>
      </ul>
      <!-- Same self-hosted Steam button as ClaimCard. -->
      <button type="button" class="settings__steam" @click="signIn">
        <img src="/steam-signin.png" :alt="$t('settings.steamSignInAlt')" width="180" height="35">
      </button>
      <p class="settings__fine">{{ $t('settings.signInNote') }}</p>
    </div>

    <template v-else>
      <!-- Account: who is signed in, and the way back to their dashboard. -->
      <div class="settings__panel settings__account">
        <img
          v-if="account.avatarUrl && !avatarFailed"
          :src="account.avatarUrl"
          alt=""
          class="settings__avatar"
          width="56"
          height="56"
          @error="avatarFailed = true"
        >
        <span v-else class="settings__avatar settings__avatar--blank" aria-hidden="true">{{ initial }}</span>
        <div class="settings__who">
          <div class="settings__eyebrow">{{ $t('settings.signedInWithSteam') }}</div>
          <div class="settings__name">{{ account.displayName || account.steamId }}</div>
          <div class="settings__steamid">{{ $t('settings.steamIdLabel') }} {{ account.steamId }}</div>
        </div>
        <router-link :to="`/user/${account.steamId}`" class="settings__btn">
          {{ $t('settings.visitDashboard') }}<span class="settings__btn-arrow" aria-hidden="true">→</span>
        </router-link>
      </div>

      <!-- Summary e-mail preferences. -->
      <div class="settings__panel">
        <div class="settings__section-head">
          <h2 class="settings__section-title">{{ $t('settings.recurrenceLabel') }}</h2>
          <p class="settings__desc">{{ $t('settings.summaryBody') }}</p>
        </div>

        <div v-if="!account.claimed" class="settings__notice">
          <span class="settings__notice-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          </span>
          <div>
            <div class="settings__notice-title">{{ $t('settings.unclaimedTitle') }}</div>
            <p class="settings__desc">{{ $t('settings.unclaimedBody') }}</p>
          </div>
        </div>

        <template v-else>
          <div class="settings__row">
            <div class="settings__row-text">
              <label class="settings__label" for="settings-email">{{ $t('settings.emailLabel') }}</label>
              <p class="settings__desc">{{ $t('settings.emailDesc') }}</p>
            </div>
            <div class="settings__row-control">
              <input id="settings-email" class="settings__input" type="email" :value="account.email" disabled>
            </div>
          </div>

          <div class="settings__row">
            <div class="settings__row-text">
              <label class="settings__label" for="settings-recurrence">{{ $t('settings.frequencyLabel') }}</label>
              <p class="settings__desc">{{ $t('settings.frequencyDesc') }}</p>
            </div>
            <div class="settings__row-control">
              <select
                id="settings-recurrence"
                v-model="recurrence"
                class="settings__input settings__select"
                :disabled="saving"
                @change="save"
              >
                <option value="weekly">{{ $t('settings.weekly') }}</option>
                <option value="disabled">{{ $t('settings.disabled') }}</option>
              </select>
              <div class="settings__under">
                <p class="settings__hint">{{ $t(recurrence === 'weekly' ? 'settings.weeklyHint' : 'settings.disabledHint') }}</p>
                <span class="settings__status" role="status" aria-live="polite">
                  <template v-if="saving">
                    <span class="settings__spinner settings__spinner--tiny" aria-hidden="true" />{{ $t('settings.saving') }}
                  </template>
                  <span v-else-if="status" :class="['settings__pill', statusError ? 'settings__pill--error' : 'settings__pill--ok']">
                    <span aria-hidden="true">{{ statusError ? '✕' : '✓' }}</span>{{ status }}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </section>
</template>

<script>
import { accountAPI } from '../services/api'
import { applyMeta } from '../utils/meta'

export default {
  name: 'Settings',
  data() {
    return {
      // loading | signedOut | ready | error
      state: 'loading',
      account: null,
      recurrence: 'weekly',
      saving: false,
      status: '',
      statusError: false,
      avatarFailed: false,
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },
    initial() {
      const name = this.account?.displayName || ''

      return name ? name.charAt(0).toUpperCase() : '?'
    },
  },
  async mounted() {
    applyMeta({ title: this.$t('settings.metaTitle') })
    try {
      const { data } = await accountAPI.get()
      this.account = data
      this.recurrence = data.recurrence || 'weekly'
      this.state = 'ready'
    } catch (err) {
      this.state = err?.response?.status === 401 ? 'signedOut' : 'error'
    }
  },
  beforeUnmount() {
    applyMeta()
  },
  methods: {
    signIn() {
      accountAPI.startLogin()
    },
    async save() {
      const previous = this.account.recurrence
      this.saving = true
      this.status = ''
      try {
        await accountAPI.saveSettings({ recurrence: this.recurrence })
        this.account.recurrence = this.recurrence
        this.status = this.$t('settings.saved')
        this.statusError = false
      } catch (_) {
        this.recurrence = previous
        this.status = this.$t('settings.saveFailed')
        this.statusError = true
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
/* A definite width: .main-content shrink-wraps its content (auto margins in a
   column flex), so a percentage width here would collapse to the text. */
.settings {
  width: min(820px, calc(100vw - 40px));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.settings__head {
  margin-bottom: 8px;
}

.settings__title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text);
}

.settings__lead {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 0.95rem;
}

/* Panel chrome follows the dashboard's .top-card: panel, border, soft shadow
   and the primary gradient bar along the top. */
.settings__panel {
  position: relative;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 22px 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
}

.settings__panel::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), rgba(102, 192, 244, 0.25));
  border-radius: 8px 8px 0 0;
}

.settings__panel--quiet {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--muted);
}

.settings__panel--error::after {
  background: linear-gradient(90deg, var(--danger), rgba(217, 83, 79, 0.25));
}

/* Signed out: a centred prompt, sized like the homepage hero card. */
.settings__signin {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px 32px;
}

.settings__signin-ico,
.settings__notice-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  border-radius: 50%;
  color: var(--primary);
  background: rgba(102, 192, 244, 0.1);
  border: 1px solid rgba(102, 192, 244, 0.3);
}

.settings__signin-ico {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}

.settings__signin-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}

.settings__perks {
  list-style: none;
  margin: 20px 0 24px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.settings__perks li {
  display: flex;
  gap: 10px;
  color: var(--text);
  font-size: 0.92rem;
  line-height: 1.45;
}

.settings__perk-tick {
  color: var(--success);
  flex: none;
}

.settings__fine {
  margin: 14px 0 0;
  max-width: 420px;
  color: var(--muted);
  font-size: 0.78rem;
  line-height: 1.5;
}

.settings__steam {
  flex: none;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 0;
  border-radius: 4px;
  transition: opacity 0.2s ease;
}

.settings__steam:hover {
  opacity: 0.85;
}

.settings__steam:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}

/* Account */
.settings__account {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.settings__avatar {
  width: 56px;
  height: 56px;
  flex: none;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(102, 192, 244, 0.35);
}

.settings__avatar--blank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #0f141a;
  color: var(--primary);
  font-weight: 700;
  font-size: 1.3rem;
}

.settings__who {
  flex: 1 1 200px;
  min-width: 0;
}

.settings__eyebrow {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.settings__name {
  margin-top: 2px;
  color: var(--text);
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.settings__steamid {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

/* Navigation, not an action: transparent, neutral text, trailing arrow, so it
   never reads as a save button (the select saves on change). */
.settings__btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  background: transparent;
  border: 1px solid var(--panel-border);
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.settings__btn:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.settings__btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.settings__btn-arrow {
  transition: transform 0.2s ease;
}

.settings__btn:hover .settings__btn-arrow {
  transform: translateX(2px);
}

/* Sections and rows */
.settings__section-head {
  padding-bottom: 18px;
}

.settings__section-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.settings__desc {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 0.86rem;
  line-height: 1.5;
}

.settings__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
  gap: 10px 32px;
  align-items: start;
  padding: 18px 0;
  border-top: 1px solid var(--panel-border);
}

.settings__row:last-child {
  padding-bottom: 2px;
}

.settings__label {
  display: block;
  color: var(--text);
  font-size: 0.92rem;
  font-weight: 600;
}

.settings__row-control {
  min-width: 0;
}

.settings__input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--panel-border);
  background: #0f141a;
  color: var(--text);
  font: inherit;
  font-size: 0.92rem;
}

.settings__input:focus {
  outline: none;
  border-color: var(--primary);
}

/* Read-only must read as read-only: no field fill, dashed edge, muted text,
   so it never looks like the editable select below it. */
input.settings__input:disabled {
  color: var(--muted);
  background: transparent;
  border-style: dashed;
  cursor: not-allowed;
}

.settings__select {
  cursor: pointer;
}

.settings__select:disabled {
  cursor: progress;
  opacity: 0.7;
}

.settings__under {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 8px;
  min-height: 24px;
}

.settings__hint {
  margin: 0;
  color: var(--muted);
  font-size: 0.8rem;
  line-height: 1.5;
}

.settings__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  font-size: 0.8rem;
}

.settings__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  animation: settings-pop 0.25s ease;
}

.settings__pill--ok {
  color: var(--success);
  background: rgba(92, 184, 92, 0.12);
  border: 1px solid rgba(92, 184, 92, 0.35);
}

.settings__pill--error {
  color: #ff6b6b;
  background: rgba(217, 83, 79, 0.12);
  border: 1px solid rgba(217, 83, 79, 0.4);
}

.settings__note--error {
  color: #ff6b6b;
}

/* Unclaimed */
.settings__notice {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px;
  border: 1px dashed var(--panel-border);
  border-radius: 8px;
  background: rgba(15, 20, 26, 0.55);
}

.settings__notice-ico {
  width: 36px;
  height: 36px;
}

.settings__notice-title {
  color: var(--text);
  font-weight: 600;
  font-size: 0.95rem;
}

.settings__spinner {
  width: 18px;
  height: 18px;
  flex: none;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: settings-spin 0.9s linear infinite;
}

.settings__spinner--tiny {
  width: 12px;
  height: 12px;
}

@keyframes settings-spin {
  to { transform: rotate(360deg); }
}

@keyframes settings-pop {
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
}

@media (max-width: 640px) {
  .settings__title {
    font-size: 1.45rem;
  }

  .settings__panel {
    padding: 18px 16px;
  }

  .settings__row {
    grid-template-columns: minmax(0, 1fr);
  }

  .settings__btn {
    width: 100%;
    justify-content: center;
  }

  .settings__signin {
    padding: 32px 16px 24px;
  }
}
</style>
